/**
 * The SCR App → Learning Diversity one-tap sign-in (the page side). Ported from Pulse's src/lib/scrappHandoff.ts.
 * The SCR App's in-app browser opens /scrapp-handoff?sso=entra[&next=/path]#login_hint=<College email>. No secret is
 * in that address.
 *
 * 1. The page asks the SCR App for { token, verifier } over `window.webkit.messageHandlers.scrappHandoff`. Only the
 *    SCR App's browser has that bridge, and it answers once, only to this origin's main frame. So a link from anywhere
 *    else can't feed someone a token (no login CSRF), and the token never touches a URL, history, Referer or log.
 * 2. It POSTs them to the ld-scrapp-handoff edge function, which verifies them and mints a normal Supabase session
 *    for the person's existing Entra account.
 * 3. supabase.auth.setSession stores it as any sign-in would. Then the app's own gate runs, the same one a Microsoft
 *    sign-in passes: the user needs a learning_diversity.profiles row (AuthContext signs out otherwise). The page runs
 *    that check itself too, and on a refusal signs out and falls back.
 *
 * Anything else (opened in Safari, no bridge, a refusal, a network error, no profile) goes to /login?sso=entra, the
 * normal Microsoft sign-in, carrying the same `next` and `login_hint`.
 */
import { readScrAppSignIn, safeNext } from './scrappSignIn';

export type HandoffCredentials = { token: string; verifier: string };
export type HandoffSession = { access_token: string; refresh_token: string };

type Bridge = { postMessage: (message: string) => Promise<unknown> };

/** The SCR App's bridge, or null outside its in-app browser. */
export function bridgeOf(w: unknown): Bridge | null {
  const handler = (w as { webkit?: { messageHandlers?: { scrappHandoff?: { postMessage?: unknown } } } } | null)
    ?.webkit?.messageHandlers?.scrappHandoff;
  return handler && typeof handler.postMessage === 'function' ? (handler as Bridge) : null;
}

const TOKEN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const VERIFIER = /^[A-Za-z0-9\-._~]{43,128}$/;

/** The bridge's reply, or null if it isn't a well-formed { token, verifier }. */
export function readCredentials(reply: unknown): HandoffCredentials | null {
  const r = reply as { token?: unknown; verifier?: unknown } | null;
  if (!r || typeof r.token !== 'string' || typeof r.verifier !== 'string') return null;
  if (r.token.length > 4096 || !TOKEN.test(r.token) || !VERIFIER.test(r.verifier)) return null;
  return { token: r.token, verifier: r.verifier };
}

/** The function's reply, or null unless it carries both tokens. */
export function readSession(body: unknown): HandoffSession | null {
  const b = body as { access_token?: unknown; refresh_token?: unknown } | null;
  return b && typeof b.access_token === 'string' && b.access_token && typeof b.refresh_token === 'string' && b.refresh_token
    ? { access_token: b.access_token, refresh_token: b.refresh_token }
    : null;
}

/** Each portal's home, as Login and AuthCallback route by profiles.role. */
export function roleHome(role: string | null | undefined): string {
  return role === 'staff' ? '/staff' : role === 'teacher' ? '/teacher' : '/login';
}

/**
 * Where the handoff lands once signed in: the SCR App's `next` (same-origin paths only), else the user's portal.
 * The route guards still decide whether the user may see `next`.
 */
export function landingPath(search: string, role: string | null | undefined): string {
  return safeNext(new URLSearchParams(search).get('next')) ?? roleHome(role);
}

/** The normal Microsoft sign-in, with the SCR App's `next` and `login_hint` (validated) carried over. */
export function fallbackLoginUrl(search: string, hash: string): string {
  // Only ever reached from the SCR App, so the fallback is always its Microsoft sign-in (sso=entra).
  const s = readScrAppSignIn('?sso=entra', hash);
  if (!s) return '/login';
  const q = new URLSearchParams({ sso: 'entra' });
  const next = safeNext(new URLSearchParams(search).get('next'));
  if (next) q.set('next', next);
  const frag = s.loginHint ? `#${new URLSearchParams({ login_hint: s.loginHint }).toString()}` : '';
  return `/login?${q.toString()}${frag}`;
}

export type HandoffDeps = {
  bridge: Bridge | null;
  /** POST the credentials to the edge function; the parsed body on 200, else null. */
  exchange: (c: HandoffCredentials) => Promise<unknown>;
  setSession: (s: HandoffSession) => Promise<boolean>;
  /** The app's post-sign-in gate on the stored session: true only when the user has a profile. */
  authorize: () => Promise<boolean>;
  /** Drops the stored session after a refusal. */
  signOut: () => Promise<void>;
  timeoutMs?: number;
};

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(null), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, () => { clearTimeout(t); resolve(null); });
  });
}

/**
 * True when a session was stored AND passed the app's gate. Never throws; every failure is false (the caller falls
 * back). A session that was stored but refused (or whose check failed) is signed out before returning false.
 */
export async function runHandoff(deps: HandoffDeps): Promise<boolean> {
  if (!deps.bridge) return false;
  const ms = deps.timeoutMs ?? 8000;
  const creds = readCredentials(await withTimeout(deps.bridge.postMessage('take'), ms));
  if (!creds) return false;
  const session = readSession(await withTimeout(deps.exchange(creds), ms));
  if (!session) return false;
  if ((await withTimeout(deps.setSession(session), ms)) !== true) {
    await withTimeout(deps.signOut(), ms);
    return false;
  }
  if ((await withTimeout(deps.authorize(), ms)) === true) return true;
  await withTimeout(deps.signOut(), ms);
  return false;
}
